import {
  Button,
  MobileTextInput,
  RightArrow,
} from "@monsterindia/global-ui-components";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  API_ENDPOINT,
  getRecaptchaSiteKey,
} from "../../../../constants/appConstant";
import { useNetwork } from "../../../../hooks/useNetwork";
import useRecaptchaV3 from "../../../../hooks/useRecaptchaV3";
import { ChangeNumberProps } from "../../../../types";
import { getSiteProps } from "../../../../utils/siteProps";
import {
  NON_INDIAN_PHONE_REGEX,
  PHONE_NUMBER_REGEX,
} from "../../../../validations/Regex";
import { encryptPayload } from "../../../utils/encryptionUtils";
import { ApplyForJobCard } from "../../common/ApplyForJobCard";
import { Heading } from "../../common/Heading";

export const ChangeNumber = React.memo(
  ({
    updateStepHandler,
    isAddNumber,
    mobileNumber,
    countryCode,
    toast,
    isRecaptchaEnabled = true,
    updateOTPIdHandler,
    updateMobileNumberAndCountryCodeHandler,
    onCTA,
    closeApop,
    jobData,
    nextApopType,
    flowType,
  }: ChangeNumberProps) => {
    const { fetchData: submitFetchAPI } = useNetwork();
    const { response: fetchResponse, fetchData: fetchAPI } = useNetwork();
    const executeRecaptcha = useRecaptchaV3(
      getRecaptchaSiteKey(window.location.host),
      isRecaptchaEnabled,
    );

    const siteConfig = getSiteProps(window.location.hostname);

    const {
      handleSubmit,
      formState: { errors },
      control,
      getValues,
      setValue,
      clearErrors,
      setError,
    } = useForm({
      defaultValues: {
        countryCode: countryCode
          ? { text: `+${countryCode}` }
          : {
              text: siteConfig?.country_isd_code
                ? `+${siteConfig?.country_isd_code}`
                : "+91",
            },
        number: mobileNumber ? mobileNumber : "",
      },
    });

    useEffect(() => {
      // MODAL style changes
      const modalContent = document.querySelector<HTMLElement>(
        ".guic-modal-content",
      );
      if (modalContent) {
        modalContent.style.overflowY = "visible";
      }

      if (countryCode) {
        setValue("countryCode", { text: `+${countryCode}` });
      }

      return () => {
        if (modalContent) {
          modalContent.style.overflowY = "auto";
        }
      };
    }, []);

    const onSubmit = async (data: any) => {
      let { reCaptchaToken, reCaptchaAction } =
        await executeRecaptcha("addnumber");
      if (!data?.number) {
        setError("number", { message: "Mobile Number is required" });
        return;
      }

      if (
        !data?.number.match(
          data.countryCode?.text && data.countryCode?.text === "+91"
            ? PHONE_NUMBER_REGEX
            : NON_INDIAN_PHONE_REGEX,
        )
      ) {
        setError("number", { message: "Invalid Mobile Number" });
        return;
      }

      const newCountryCode = data.countryCode?.text
        ? (data.countryCode?.text?.slice(1) ?? countryCode)
        : countryCode;
      const mobileNumber = data.number;
      const username = `+${newCountryCode}${mobileNumber}`;
      const apiData = isRecaptchaEnabled
        ? { username, reCaptchaToken, reCaptchaAction }
        : { username };
      const encryptedPayload = encryptPayload(apiData);

      const res = await submitFetchAPI(
        API_ENDPOINT().seekerProfile.sendOTPMiddleware,
        "POST",
        {},
        { payload: encryptedPayload },
      );

      if (!res.error) {
        if ((res?.data as any)?.id) {
          onCTA && onCTA(isAddNumber ? "ADD_NUMBER" : "CHANGE_NUMBER");
          toast({
            showToast: true,
            text: "OTP sent successfully",
            type: "success",
          });
          updateMobileNumberAndCountryCodeHandler(newCountryCode, mobileNumber);
          updateOTPIdHandler((res?.data as any)?.id);
          if (siteConfig?.site_map !== "rexmonster") {
            const validateRes = await fetchAPI(
              API_ENDPOINT().seekerProfile.validateProfileMiddleware,
              "GET",
            );

            if (validateRes.data) {
              const { actionType: apiActionType, ...rest } =
                validateRes.data as any;

              nextApopType(apiActionType, rest);
            } else {
              toast({
                showToast: true,
                text: "Something went wrong",
                type: "error",
              });
            }
          }

          updateStepHandler("MOBILE_OTP");
        } else {
          toast({
            showToast: true,
            text: "Error in sending OTP",
            type: "error",
          });
        }
      } else {
        toast({
          showToast: true,
          text:
            (res.error as any)?.error?.message?.errorMessage ??
            "Something went wrong",
          type: "error",
        });
      }
    };

    const countryISD = getValues("countryCode")?.text
      ? getValues("countryCode")?.text
      : countryCode
        ? `+${countryCode}`
        : siteConfig?.country_isd_code
          ? `+${siteConfig?.country_isd_code}`
          : "+91";

    return (
      <>
        <Heading
          headingText={
            flowType === "PROFILE_NUDGE" && jobData
              ? "Job Application"
              : isAddNumber
                ? "Add contact details"
                : "Update contact details"
          }
          onCloseBtnClickHandler={closeApop}
          isMobile={window.innerWidth < 786}
        />

        <div
          className="l1-relative l1-mb-16 l1-mt-14 l1-flex l1-flex-col l1-gap-6 md:l1-mb-12 md:l1-mt-10"
          id="changeNumberForm"
        >
          {jobData ? (
            <ApplyForJobCard
              jobId={jobData.jobId}
              jobTitle={jobData.jobTitle}
              companyName={jobData.companyName}
              companyLogoUrl={jobData.companyLogoUrl}
            />
          ) : null}

          <div className="l1-flex l1-flex-col l1-gap-4">
            <h4 className="l1-text-2xl l1-font-bold l1-text-content-primary">
              Mobile Number
            </h4>

            <p className="l1-text-sm l1-text-content-secondary">
              {isAddNumber ? "Add" : "Verify"} your number for recruiters to
              contact you.
            </p>

            <form
              className="l1-mt-2 l1-w-full"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <Controller
                name="number"
                control={control}
                rules={{
                  required: "Mobile Number is required",
                }}
                render={({ field }) => {
                  return (
                    <MobileTextInput
                      mobileNumber={field.value ?? mobileNumber ?? ""}
                      setMobileNumber={(value: string) => {
                        clearErrors("number");
                        setValue("number", value);
                      }}
                      required={true}
                      countryCode={{
                        text: countryISD,
                      }}
                      setCountryCode={(value: unknown) => {
                        setValue("countryCode", {
                          text: (value as any)?.text,
                        });
                        setValue("number", "");
                      }}
                      errorText={errors?.number ? errors?.number?.message : ""}
                    />
                  );
                }}
              />

              <div className="l1-fixed l1-bottom-0 l1-left-0 l1-z-10 l1-flex l1-w-full l1-max-w-full l1-gap-2 l1-bg-white l1-px-4 l1-py-3 l1-shadow-black-sm md:!l1-px-8 md:l1-py-4">
                <div className="l1-ml-auto l1-w-max">
                  <Button
                    className="!l1-w-full"
                    label={isAddNumber ? "Add Mobile Number" : "Continue"}
                    rightIcon={<RightArrow width={16} height={16} />}
                    modifier="primary"
                    type="submit"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  },
);
